import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import CardHead from "../utils/CardHead";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
);

const ProcurementCharts = ({ data = [] }) => {
  // 📊 Bar Chart Data (Procurement)
  const barData = useMemo(() => {
    const sorted = data
      .map((d) => ({
        dist: d.dist,
        proc: Math.max(0, d.amc * 4 - d.stock - d.pipeline),
      }))
      .filter((d) => d.proc > 0)
      .sort((a, b) => b.proc - a.proc)
      .slice(0, 15);

    return {
      labels: sorted.map((d) => d.dist),
      datasets: [
        {
          label: "Procurement Qty",
          data: sorted.map((d) => d.proc),
          backgroundColor: sorted.map((d) =>
            d.proc > 3000 ? "#dc2626" : d.proc > 1500 ? "#f97316" : "#f59e0b",
          ),
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  const barOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        clamp: true, // keeps label inside chart area
        clip: false, // prevents cutting off

        anchor: "end",

        align: function (context) {
          const value = context.dataset.data[context.dataIndex];
          const max = Math.max(...context.dataset.data);

          // If bar is too large → move label inside
          return value > max * 0.85 ? "left" : "right";
        },

        color: function (context) {
          const value = context.dataset.data[context.dataIndex];
          const max = Math.max(...context.dataset.data);

          // Inside bar → white text
          return value > max * 0.85 ? "#ffffff" : "#0f172a";
        },

        formatter: (value) => value.toLocaleString(),

        font: {
          size: 12,
          weight: "bold",
        },
      },
    },
    scales: {
      x: { grid: { color: "#f1f5f9" } },
      y: {},
    },
  };

  // 🍩 Doughnut Chart Data (Coverage)
  const doughnutData = useMemo(() => {
    let gt4 = 0,
      m2to4 = 0,
      m1to2 = 0,
      lt1 = 0;

    data.forEach((d) => {
      const cov = d.stock / d.amc;
      if (cov >= 4) gt4++;
      else if (cov >= 2) m2to4++;
      else if (cov >= 1) m1to2++;
      else lt1++;
    });

    return {
      labels: [
        `> 4 Months (${gt4})`,
        `2–4 Months (${m2to4})`,
        `1–2 Months (${m1to2})`,
        `< 1 Month (${lt1})`,
      ],
      datasets: [
        {
          data: [gt4, m2to4, m1to2, lt1],
          backgroundColor: ["#16a34a", "#f59e0b", "#f97316", "#dc2626"],
          borderWidth: 2,
        },
      ],
    };
  }, [data]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "55%",

    plugins: {
      legend: {
        position: "bottom",
      },

      datalabels: {
        clamp: true,
        clip: false,

        formatter: (value, context) => {
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percent = ((value / total) * 100).toFixed(0);
          return value > 0 ? `${value} (${percent}%)` : "";
        },

        color: "#fff",

        font: {
          size: 11,
          weight: "bold",
        },

        anchor: "center",
        align: "center",
      },
    },
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "14px",
        marginTop: "20px",
      }}
    >
      {/* 📊 Bar Chart */}
      <div className="card">
        <CardHead
          title="📊 District-wise Procurement Requirement"
          subtitle="Districts requiring procurement"
        ></CardHead>
        <div style={{ height: "380px", padding: "20px" }}>
          <Bar data={barData} options={barOptions} />
        </div>
      </div>

      {/* 🍩 Doughnut Chart */}
      <div className="card">
        <CardHead
          title="📊 Stock Coverage Distribution"
          subtitle="Grouped by stock coverage"
        ></CardHead>
        <div style={{ height: "380px", padding: "20px" }}>
          <Doughnut data={doughnutData} options={doughnutOptions} />
        </div>
      </div>
    </div>
  );
};

export default ProcurementCharts;
